import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import AuthContext from './authContext';
import useBranch from '../hooks/useBranch';
import useUser from '../hooks/useUser';

// Shared pizza branch/location selection.
//
// Both the Pizza view and the globally mounted PizzaAttentionWatcher must agree
// on which location the user is currently working with, so notifications are
// only raised for that location. The selection is persisted in localStorage
// (per user) and exposed via `notificationsBranchId`, which is null whenever no
// concrete location is selected (i.e. "Alle Standorte") so no reminders go out.
//
// Rules:
//   - exactly one assigned branch  -> always that branch (no selector)
//   - multiple assigned branches   -> user picks one, persisted; reminders for it
//   - no assigned branch           -> "Alle Standorte" + all branches; reminders
//                                     only for an explicitly selected branch,
//                                     none while "Alle Standorte" is active

const STORAGE_PREFIX = 'pizza:selectedBranchId:';

const PizzaBranchContext = createContext({
  branchOptions: [],
  selectedBranchId: '',
  setSelectedBranchId: () => {},
  showBranchSelect: false,
  branchesReady: false,
  notificationsBranchId: null,
});

export const usePizzaBranch = () => useContext(PizzaBranchContext);

const buildBranchOptions = async ({ getMyBranches, getBranches }) => {
  const own = await getMyBranches();
  const ownBranchIds = own?.branchIds || [];
  const ownBranches = own?.branches || [];

  if (ownBranchIds.length === 1) {
    return {
      selectedBranchId: ownBranchIds[0],
      showBranchSelect: false,
      options: ownBranches.map(branch => ({
        id: branch._id,
        label: branch.locationName || branch.longName || 'Standort',
      })),
    };
  }

  if (ownBranchIds.length > 1) {
    return {
      selectedBranchId: ownBranchIds[0] || '',
      showBranchSelect: true,
      options: ownBranches.map(branch => ({
        id: branch._id,
        label: branch.locationName || branch.longName || 'Standort',
      })),
    };
  }

  const allBranches = await getBranches();
  return {
    selectedBranchId: '',
    showBranchSelect: true,
    options: [
      { id: '', label: 'Alle Standorte' },
      ...(allBranches || []).map(branch => ({
        id: branch._id,
        label: branch.locationName || branch.longName || 'Standort',
      })),
    ],
  };
};

export const PizzaBranchProvider = ({ children }) => {
  const authCtx = useContext(AuthContext);
  const { getBranches } = useBranch();
  const { getMyBranches } = useUser();

  const token = authCtx?.token;
  const canRead = authCtx?.checkRightAny?.('internalBooking:read:own');
  const userId = authCtx?.currentUser?.userId || 'anon';
  const storageKey = `${STORAGE_PREFIX}${userId}`;

  const [branchOptions, setBranchOptions] = useState([]);
  const [showBranchSelect, setShowBranchSelect] = useState(false);
  const [branchesReady, setBranchesReady] = useState(false);
  const [selectedBranchId, setSelectedBranchIdState] = useState('');

  const setSelectedBranchId = useCallback(
    next => {
      const value = next || '';
      setSelectedBranchIdState(value);
      try {
        window.localStorage.setItem(storageKey, value);
      } catch (_) {
        // Ignore storage failures (e.g. private mode).
      }
    },
    [storageKey]
  );

  useEffect(() => {
    if (!token || !canRead) {
      setBranchesReady(false);
      setBranchOptions([]);
      setShowBranchSelect(false);
      setSelectedBranchIdState('');
      return undefined;
    }

    let cancelled = false;
    (async () => {
      let next;
      try {
        next = await buildBranchOptions({ getMyBranches, getBranches });
      } catch (_) {
        next = {
          selectedBranchId: '',
          showBranchSelect: true,
          options: [{ id: '', label: 'Alle Standorte' }],
        };
      }
      if (cancelled) return;

      const validIds = new Set((next.options || []).map(option => option.id));
      let initial = next.selectedBranchId;
      // Only restore a persisted choice when a selector is actually shown; a
      // single assigned branch always resolves to that branch.
      if (next.showBranchSelect) {
        let persisted = null;
        try {
          persisted = window.localStorage.getItem(storageKey);
        } catch (_) {
          persisted = null;
        }
        if (persisted !== null && validIds.has(persisted)) initial = persisted;
      }

      setBranchOptions(next.options || []);
      setShowBranchSelect(next.showBranchSelect);
      setSelectedBranchIdState(initial);
      setBranchesReady(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, canRead, storageKey]);

  // The location reminders should be scoped to. Null means "no concrete
  // location selected" (Alle Standorte) and therefore no notifications.
  const notificationsBranchId = useMemo(() => {
    if (!branchesReady) return null;
    return selectedBranchId ? selectedBranchId : null;
  }, [branchesReady, selectedBranchId]);

  const value = useMemo(
    () => ({
      branchOptions,
      selectedBranchId,
      setSelectedBranchId,
      showBranchSelect,
      branchesReady,
      notificationsBranchId,
    }),
    [
      branchOptions,
      selectedBranchId,
      setSelectedBranchId,
      showBranchSelect,
      branchesReady,
      notificationsBranchId,
    ]
  );

  return <PizzaBranchContext.Provider value={value}>{children}</PizzaBranchContext.Provider>;
};

export default PizzaBranchContext;
